import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import RelatedPages from '../components/sections/partials/RelatedPages';
import { useLanguage } from '../contexts/LanguageContext';
import { seoContent } from '../seo/content';
import { glossaryJsonLd } from '../seo/schema';
import { localizedPath } from '../utils/localizedPath';

/**
 * The glossary: every term an institution runs into when it is deciding whether
 * to put an AI agent inside a course, defined in plain language.
 *
 * The copy lives in `seoContent[lang].glossary` (src/seo/content.js) as
 * `{ term, slug, definition }`. This view owns no definitions; it owns the page
 * chrome and the page's own title/description, which content.js does not carry
 * for the glossary.
 *
 * Every term is an `<h2 id="{slug}">`, so a single definition is linkable and
 * citable on its own: /glossary#rag-retrieval-augmented-generation. The same
 * slugs become the `@id`/`url` of the `DefinedTerm` nodes in the JSON-LD, which
 * is the type answer engines read for a glossary.
 */

/** Page title and description. content.js has none for the glossary. */
const META = {
  en: {
    title: 'AI Agent Glossary for Education | E-comienza',
    description:
      'Plain-language definitions of the AI terms behind course agents: RAG, LLM, ' +
      'grounding, hallucination, source attribution, Moodle, LMS and vector index.',
    breadcrumb: 'Glossary',
    name: 'AI Agent Glossary for Education',
    h1: 'AI Agent Glossary',
    intro:
      'These are the words that come up when a university, an institute or a training ' +
      'academy starts deciding whether to put an AI agent inside a course. Each one is ' +
      'defined here in plain language, without marketing, so a conversation about an ' +
      'agent can be had on the same terms by the people who teach, the people who buy ' +
      'and the people who build.',
    moodleLead: 'To see these terms in production, read how the',
    moodleLink: 'Moodle AI agent',
    moodleTail: 'answers students from a course curriculum and cites the unit and page.',
    indexLabel: 'Terms on this page',
  },
  es: {
    title: 'Glosario de agentes de IA para educación | E-comienza',
    description:
      'Definiciones en lenguaje claro de los términos de IA detrás de los agentes de ' +
      'curso: RAG, LLM, anclaje, alucinación, atribución, Moodle, LMS e índice vectorial.',
    breadcrumb: 'Glosario',
    name: 'Glosario de agentes de IA para educación',
    h1: 'Glosario de agentes de IA',
    intro:
      'Estas son las palabras que aparecen cuando una universidad, un instituto o una ' +
      'academia de formación empieza a decidir si pone un agente de IA dentro de un ' +
      'curso. Cada una está definida aquí en lenguaje claro, sin marketing, para que la ' +
      'conversación sobre un agente se pueda tener en los mismos términos entre quienes ' +
      'enseñan, quienes deciden la compra y quienes lo construyen.',
    moodleLead: 'Para ver estos términos en producción, lee cómo el',
    moodleLink: 'agente de IA para Moodle',
    moodleTail:
      'responde a los alumnos con el material del curso y cita la unidad y la página.',
    indexLabel: 'Términos de esta página',
  },
};

const GlossaryPage = () => {
  const { lang } = useLanguage();

  const content = seoContent[lang] || seoContent.en;
  const entries = content.glossary || [];
  const t = META[lang] || META.en;

  return (
    <>
      <Seo
        title={t.title}
        description={t.description}
        routeKey="glossary"
        jsonLd={glossaryJsonLd(entries, t, lang)}
      />

      <article className="section content-page">
        <div className="container">
          <div className="section-inner">
            <header className="content-page-header">
              <h1 className="mt-0 mb-16">{t.h1}</h1>
              <p className="content-page-intro mb-0">{t.intro}</p>
              <p className="content-page-intro mb-0 mt-16">
                {t.moodleLead}{' '}
                <Link to={localizedPath('moodleAgent', lang)}>{t.moodleLink}</Link>{' '}
                {t.moodleTail}
              </p>
            </header>

            {/* A jump list, not decoration: with fourteen definitions on one page it is
                how a reader (and an answer engine following anchors) reaches one term. */}
            <nav className="glossary-index" aria-label={t.indexLabel}>
              <span className="section-label">{t.indexLabel}</span>
              <ul className="list-reset">
                {entries.map((entry) => (
                  <li key={entry.slug}>
                    <a href={`#${entry.slug}`}>{entry.term}</a>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="glossary-list">
              {entries.map((entry) => (
                <section className="glossary-item" key={entry.slug}>
                  {/* The id is the anchor a link or a citation points at. */}
                  <h2 id={entry.slug} className="mt-0 mb-8">
                    {entry.term}
                  </h2>
                  <p className="mb-0">{entry.definition}</p>
                </section>
              ))}
            </div>

            <RelatedPages from="glossary" />
          </div>
        </div>
      </article>
    </>
  );
};

export default GlossaryPage;
